import { useCallback, useMemo } from 'react';
import DrawerExtraButtons from 'components/DrawerExtraButtons';
import useSelectResource from 'hooks/useSelectResource';
import { getText } from 'i18n/intl';
import {
  availabilityRelationMapSelector,
  changeAvailabilityRelation,
  checkAvailabilityRelationSaveDisabled,
  deleteAvailabilityRelation,
  discardChanges,
  firstAvailabilityRelationSelector,
  saveAvailabilityRelation,
  secondAvailabilityRelationSelector,
  setFirstAvailabilityRelation,
  setSecondAvailabilityRelation,
} from 'slices/availabilityRelation.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { DrawerCallback } from 'types/utils.type';
import type { TAvailabilityRelationType } from '@timeedit/types';
import { TEDrawer } from '@timeedit/ui-components';
import AvailabilityRelation from './AvailabilityRelation';

interface Props {
  confirmDiscardPaddingChanges: (
    isFirstDrawer: boolean,
    actionFn: () => void
  ) => void;
}

const AvailabilityRelationDrawer = ({
  confirmDiscardPaddingChanges,
}: Props) => {
  const dispatch = useAppDispatch();

  const firstAvailabilityRelation = useAppSelector(
    firstAvailabilityRelationSelector
  );
  const secondAvailabilityRelation = useAppSelector(
    secondAvailabilityRelationSelector
  );
  const availabilityRelationMap: Record<
    number | string,
    TAvailabilityRelationType
  > = useAppSelector(availabilityRelationMapSelector);

  const setFirstResource = useCallback(
    (availabilityRelation: TAvailabilityRelationType) =>
      dispatch(setFirstAvailabilityRelation(availabilityRelation)),
    [dispatch]
  );

  const setSecondResource = useCallback(
    (availabilityRelation: TAvailabilityRelationType) =>
      dispatch(setSecondAvailabilityRelation(availabilityRelation)),
    [dispatch]
  );

  useSelectResource(
    'integer',
    availabilityRelationMap,
    firstAvailabilityRelation,
    secondAvailabilityRelation,
    setFirstResource,
    setSecondResource
  );

  const firstAvailabilityRelationSaveDisabled = useMemo(
    () => dispatch(checkAvailabilityRelationSaveDisabled(true)),
    [firstAvailabilityRelation]
  );

  const secondAvailabilityRelationSaveDisabled = useMemo(
    () => dispatch(checkAvailabilityRelationSaveDisabled(false)),
    [secondAvailabilityRelation]
  );

  const onDelete = useCallback(
    (availabilityRelation: TAvailabilityRelationType) => {
      dispatch(deleteAvailabilityRelation(availabilityRelation.id));
    },
    []
  );

  const onSave = useCallback(
    (availabilityRelation: TAvailabilityRelationType) => {
      dispatch(saveAvailabilityRelation(availabilityRelation));
    },
    []
  );

  const onClose = useCallback(
    (isFirstDrawer: boolean) =>
      confirmDiscardPaddingChanges(isFirstDrawer, () => {
        if (isFirstDrawer) {
          dispatch(setFirstAvailabilityRelation(undefined));
        } else {
          dispatch(setSecondAvailabilityRelation(undefined));
        }
      }),
    []
  );

  const onChange = useCallback<DrawerCallback<TAvailabilityRelationType>>(
    (isFirstDrawer, key, value) =>
      dispatch(changeAvailabilityRelation({ isFirstDrawer, key, value })),
    []
  );

  return (
    <TEDrawer
      expandWidth="70vw"
      firstDrawer={{
        title: firstAvailabilityRelation?.name || '',
        content: (
          <AvailabilityRelation
            availabilityRelation={firstAvailabilityRelation}
            onChange={(key, value) => onChange(true, key, value)}
          />
        ),
        drawerOpen: !!firstAvailabilityRelation,
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText(
              'availability_relation_confirmation_delete'
            )}
            handleDelete={() => onDelete(firstAvailabilityRelation!)}
            handleDiscard={() =>
              dispatch(
                discardChanges({
                  isFirstDrawer: true,
                  id: firstAvailabilityRelation!.id,
                })
              )
            }
            handleSave={() => onSave(firstAvailabilityRelation!)}
            isSaveDisable={firstAvailabilityRelationSaveDisabled as any}
          />
        ),
        setDrawerOpen: (open) => !open && onClose(true),
      }}
      secondDrawer={{
        title: secondAvailabilityRelation?.name || '',
        content: (
          <AvailabilityRelation
            availabilityRelation={secondAvailabilityRelation}
            onChange={(key, value) => onChange(false, key, value)}
          />
        ),
        drawerOpen: !!secondAvailabilityRelation,
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText(
              'availability_relation_confirmation_delete'
            )}
            handleDelete={() => onDelete(secondAvailabilityRelation!)}
            handleDiscard={() =>
              dispatch(
                discardChanges({
                  isFirstDrawer: false,
                  id: secondAvailabilityRelation!.id,
                })
              )
            }
            handleSave={() => onSave(secondAvailabilityRelation!)}
            isSaveDisable={secondAvailabilityRelationSaveDisabled as any}
          />
        ),
        setDrawerOpen: (open) => !open && onClose(false),
      }}
    />
  );
};

export default AvailabilityRelationDrawer;
